import React, { useRef, useEffect, useMemo } from "react";
import { Clock, Sun, Sunrise, Sunset, Moon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { formatSlotLabel } from "../lib/dateLocal";

const GROUPS = [
  { key: "morning",   label: "Morning",   icon: Sunrise, startH: 6,  endH: 12 },
  { key: "afternoon", label: "Afternoon", icon: Sun,     startH: 12, endH: 17 },
  { key: "evening",   label: "Evening",   icon: Sunset,  startH: 17, endH: 22 },
  { key: "night",     label: "Night",     icon: Moon,    startH: 22, endH: 6  },
];

const AFTER_HOURS_START = 22;
const AFTER_HOURS_END = 6;

function isAfterHours(slot) {
  const h = Number(slot.split(":")[0]);
  return h >= AFTER_HOURS_START || h < AFTER_HOURS_END;
}

function slotInGroup(slot, group) {
  const h = Number(slot.split(":")[0]);
  if (group.key === "night") return h >= group.startH || h < group.endH;
  return h >= group.startH && h < group.endH;
}

/**
 * Time slot picker with 12h labels grouped by time of day.
 *
 * Props:
 *   value        — "HH:MM" 24h string
 *   onChange      — (newValue: string) => void
 *   slots        — array of "HH:MM" strings (available slots)
 *   disabled     — boolean
 *   markAfterHours — show a subtle indicator on 22:00–06:00 slots
 *   testid       — data-testid for the trigger button
 */
export default function TimeSlotPicker({
  value,
  onChange,
  slots = [],
  disabled = false,
  markAfterHours = false,
  testid,
}) {
  const [open, setOpen] = React.useState(false);
  const selectedRef = useRef(null);

  useEffect(() => {
    if (open && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: "nearest", behavior: "instant" });
    }
  }, [open]);

  const grouped = useMemo(() => {
    return GROUPS.map((g) => ({
      ...g,
      slots: slots.filter((s) => slotInGroup(s, g)),
    })).filter((g) => g.slots.length > 0);
  }, [slots]);

  const displayLabel = value ? formatSlotLabel(value) : "Select time";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="zc-input h-11 px-3 text-sm text-left flex items-center justify-between w-full min-w-0 gap-2"
          data-testid={testid}
        >
          <span className={value ? "" : "text-[var(--zc-text-3)]"}>
            {displayLabel}
          </span>
          <Clock size={14} className="text-[var(--zc-text-3)] shrink-0" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[260px] p-0 bg-white border border-[var(--zc-border)] z-[100] max-h-[320px] overflow-y-auto"
        align="start"
        sideOffset={6}
      >
        {grouped.map((g) => {
          const Icon = g.icon;
          return (
            <div key={g.key}>
              <div className="zc-timeslot-group-header">
                <Icon size={13} />
                <span>{g.label}</span>
              </div>
              <div className="grid grid-cols-3 gap-1 px-2 pb-2">
                {g.slots.map((s) => {
                  const selected = s === value;
                  const afterHrs = markAfterHours && isAfterHours(s);
                  return (
                    <button
                      key={s}
                      ref={selected ? selectedRef : undefined}
                      type="button"
                      onClick={() => { onChange?.(s); setOpen(false); }}
                      className={
                        "zc-timeslot-btn" +
                        (selected ? " zc-timeslot-active" : "") +
                        (afterHrs ? " zc-timeslot-afterhours" : "")
                      }
                      aria-pressed={selected}
                      data-testid={testid ? `${testid}-slot-${s}` : undefined}
                    >
                      {formatSlotLabel(s)}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        {slots.length === 0 && (
          <div className="px-3 py-4 text-sm text-[var(--zc-text-3)] text-center">
            No time slots available
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
